import type {
    CatalogProgram,
    CatalogSearchDirection,
    CountryIsoCode,
    DashboardProgram,
    ExclusionType,
    LanguageIsoCode,
    Program,
    ProgramMasterTechnicalDetails,
    ProgramQuality,
    SearchCatalogProgramsFilterInput,
    SearchCatalogProgramsOrderByInput,
    SearchDashboardProgramsFilterInput,
    SearchProgramsOrderByInput,
    UpdateProgramInput,
} from '@/graphql/graphql';
import {
    GET_EPISODES_ADMIN,
    GET_EPISODES_CATALOG,
    GET_EPISODES_DASHBOARD,
    GET_EPISODES_PRICE,
    GET_PROGRAMS_ADMIN,
    GET_PROGRAMS_CATALOG,
    GET_PROGRAMS_DASHBOARD,
    GET_PROGRAM_ADMIN,
    GET_PROGRAM_CATALOG,
    GET_PROGRAM_DASHBOARD,
    UPDATE_PROGRAM_DASHBOARD,
} from '@/graphql/queries/programs.graphql';
import {
    type PaginationArgs,
    asLowercaseStringArray,
    asStringArray,
    getCurrentLocale,
    hasValue,
    localizedMap,
    parseRange,
    toDateRange,
    toHeaderContext,
    toNumberRange,
    toPagination,
    toSearchDirection,
} from '@/lib/api/helpers';
import { apolloClient } from '@/lib/apollo-client';

export type ProgramReadContext = 'catalog' | 'dashboard' | 'admin';
export type ProgramStatus = 'not_published' | 'published';

type ProgramEditorializationLike = {
    title?: string | null;
    synopsis?: string | null;
    keywords?: string[] | null;
};

type RawExcludedCountry = {
    countryCode?: CountryIsoCode;
    countryISOCode?: CountryIsoCode;
    countryIsoCode?: CountryIsoCode;
    exclusionType: ExclusionType;
};

type ApiProgramCategory = {
    id: string;
    title: string;
};

type ProgramFilesLike = {
    coverUrl?: string | null;
    creditRollUrl?: string | null;
    masterSubtitleUrl?: string | null;
    masterTechnicalDetails?: ProgramMasterTechnicalDetails | null;
    masterUrl?: string | null;
    trailerSubtitleUrl?: string | null;
    trailerUrl?: string | null;
};

export type ApiProgram = Omit<Program, 'category' | 'parent'> & {
    category: ApiProgramCategory;
    editorialization?: ProgramEditorializationLike | null;
    emptyEpisodes?: number | null;
    lifecycle?: {
        demo?: boolean;
        published?: boolean;
        rightExclusivity?: boolean;
        validated?: boolean;
    } | null;
    masterTechnicalDetails?: ProgramMasterTechnicalDetails | null;
    masterSubtitleUrl?: string | null;
    masterUrl?: string | null;
    originalLanguage?: LanguageIsoCode | null;
    parent?: ApiProgram | null;
    publishedAt?: string | null;
    rightExclusivity?: boolean | null;
    sourceSku?: string | null;
    trailerSubtitleUrl?: string | null;
    trailerUrl?: string | null;
    type?: string | null;
};

type RawProgram = Partial<CatalogProgram & DashboardProgram> & {
    coverUrl?: string | null;
    creditRollUrl?: string | null;
    editorialization?: ProgramEditorializationLike | null;
    excludedCountries?: RawExcludedCountry[] | null;
    files?: ProgramFilesLike | null;
    lifecycle?: ApiProgram['lifecycle'];
    masterSubtitleUrl?: string | null;
    masterTechnicalDetails?: ProgramMasterTechnicalDetails | null;
    masterUrl?: string | null;
    originalLanguage?: LanguageIsoCode | null;
    parent?: RawProgram | null;
    publicSeller?: { legalName: string } | null;
    rightExclusivity?: boolean | null;
    seller?: { legalName: string } | null;
    trailerSubtitleUrl?: string | null;
    trailerUrl?: string | null;
};

export type FetchProgramsNamespaceArgs = PaginationArgs & {
    filters?: {
        [key: string]: unknown;
    };
    sortBy?: string;
    excludedGenres?: string[];
    sortOrder?: 'asc' | 'desc' | 'random' | 'ASC' | 'DESC' | 'RANDOM';
    random?: boolean;
    skipAuth?: boolean;
    locale?: string;
};

export type FetchSubProgramsListArgs = PaginationArgs & {
    id: string;
    filters?: {
        [key: string]: unknown;
    };
    skipAuth?: boolean;
    locale?: string;
};

const resolveDashboardStatusFlags = (status: unknown): { published?: boolean } => {
    switch (status) {
        case 'not_published':
            return { published: false };
        case 'published':
            return { published: true };
        default:
            return {};
    }
};

const asCountryArray = (value: unknown): string[] | undefined => asStringArray(value);

const buildCatalogFilter = (
    filters: Record<string, unknown> = {},
    excludedGenres?: string[],
): SearchCatalogProgramsFilterInput => {
    const episodesQuantity = parseRange<number>(filters.episodesQuantity);
    const firstBroadcastDate = toDateRange(filters.minimumFirstBroadcastDate, filters.maximumFirstBroadcastDate);
    const price = toNumberRange(filters.minimumPrice, filters.maximumPrice);

    return {
        ...(hasValue(filters.audioLanguages) && { audioLanguages: asLowercaseStringArray(filters.audioLanguages) }),
        ...(hasValue(filters.awards) && { awards: String(filters.awards) }),
        ...(hasValue(filters.casting) && { casting: String(filters.casting) }),
        ...(hasValue(filters.categoryIds) && { category: asStringArray(filters.categoryIds) }),
        ...(hasValue(filters.category) && { category: asStringArray([filters.category]) }),
        ...(hasValue(filters.directors) && { directors: String(filters.directors) }),
        ...(episodesQuantity && {
            episodesQuantity: toNumberRange(episodesQuantity.min, episodesQuantity.max),
        }),
        ...((excludedGenres?.length || hasValue(filters.excludedGenres)) && {
            excludedGenres: excludedGenres?.length ? excludedGenres : asStringArray(filters.excludedGenres),
        }),
        ...(firstBroadcastDate && { firstBroadcastDate }),
        ...(hasValue(filters.genres) && { genre: asStringArray(filters.genres) }),
        ...(hasValue(filters.keywords) && { keywords: String(filters.keywords) }),
        ...('parentId' in filters && { parentId: filters.parentId as string | null }),
        ...(price && { price }),
        ...(hasValue(filters.productionCountries) && {
            productionCountries: asCountryArray(filters.productionCountries),
        }),
        ...(hasValue(filters.quality) && { quality: filters.quality as ProgramQuality }),
        ...(hasValue(filters.sellerId) && { seller: String(filters.sellerId) }),
        ...(hasValue(filters.synopsis) && { synopsis: String(filters.synopsis) }),
        ...(hasValue(filters.title) && { title: String(filters.title) }),
        ...(hasValue(filters.titles) && { title: String(filters.titles) }),
        ...(hasValue(filters.type) && { type: asStringArray(filters.type) }),
    };
};

const buildDashboardFilter = (filters: Record<string, unknown> = {}): SearchDashboardProgramsFilterInput => {
    const statusFlags = resolveDashboardStatusFlags(filters.status);
    const duration = parseRange<number>(filters.duration);
    const episodesQuantity = parseRange<number>(filters.episodesQuantity);

    return {
        ...(hasValue(filters.audioLanguages) && { audioLanguages: asLowercaseStringArray(filters.audioLanguages) }),
        ...(hasValue(filters.categoryIds) && { category: asStringArray(filters.categoryIds) }),
        ...(hasValue(filters.category) && { category: asStringArray([filters.category]) }),
        ...(duration && { duration: toNumberRange(duration.min, duration.max) }),
        ...(episodesQuantity && { episodesQuantity: toNumberRange(episodesQuantity.min, episodesQuantity.max) }),
        ...(hasValue(filters.sku) && { identifier: Number(filters.sku) }),
        ...(hasValue(filters.identifier) && { identifier: Number(filters.identifier) }),
        ...(toNumberRange(filters.minimumLicensesQuantity, filters.maximumLicensesQuantity) && {
            licensesQuantity: toNumberRange(filters.minimumLicensesQuantity, filters.maximumLicensesQuantity),
        }),
        ...('parentId' in filters && { parentId: filters.parentId as string | null }),
        ...(hasValue(filters.productionCountries) && {
            productionCountries: asCountryArray(filters.productionCountries),
        }),
        ...(hasValue(filters.published) && { published: Boolean(filters.published) }),
        ...(hasValue(filters.validated) && { validated: Boolean(filters.validated) }),
        ...(hasValue(filters.title) && { title: String(filters.title) }),
        ...(hasValue(filters.titles) && { title: String(filters.titles) }),
        ...(hasValue(filters.type) && { type: asStringArray(filters.type) }),
        ...statusFlags,
    };
};

const catalogSortFieldMap: Record<string, keyof SearchCatalogProgramsOrderByInput> = {
    casting: 'casting',
    createdAt: 'createdAt',
    directors: 'directors',
    duration: 'duration',
    episodeNumber: 'episodeNumber',
    episodesQuantity: 'episodesQuantity',
    firstBroadcastDate: 'firstBroadcastDate',
    identifier: 'identifier',
    keywords: 'keywords',
    licensesQuantity: 'licensesQuantity',
    price: 'price',
    productionCountry: 'productionCountry',
    sku: 'identifier',
    synopsis: 'synopsis',
    title: 'title',
    titles: 'title',
};

const searchSortFieldMap: Record<string, keyof SearchProgramsOrderByInput> = {
    casting: 'casting',
    createdAt: 'createdAt',
    directors: 'directors',
    duration: 'duration',
    episodeNumber: 'episodeNumber',
    episodesQuantity: 'episodesQuantity',
    firstBroadcastDate: 'firstBroadcastDate',
    identifier: 'identifier',
    licensesQuantity: 'licensesQuantity',
    productionCountry: 'productionCountry',
    published: 'published',
    sellerLegalName: 'sellerLegalName',
    sku: 'identifier',
    title: 'title',
    titles: 'title',
    updatedAt: 'updatedAt',
    validated: 'validated',
};

const buildCatalogOrderBy = ({
    random,
    sortBy,
    sortOrder,
}: Pick<FetchProgramsNamespaceArgs, 'random' | 'sortBy' | 'sortOrder'>):
    | SearchCatalogProgramsOrderByInput
    | undefined => {
    if (random || sortOrder === 'random' || sortOrder === 'RANDOM') {
        return { createdAt: 'RANDOM' as CatalogSearchDirection };
    }

    if (!sortBy || !catalogSortFieldMap[sortBy]) {
        return undefined;
    }

    const direction = toSearchDirection(sortOrder);
    return direction
        ? ({
              [catalogSortFieldMap[sortBy]]: direction as unknown as CatalogSearchDirection,
          } as SearchCatalogProgramsOrderByInput)
        : undefined;
};

const buildSearchOrderBy = ({
    sortBy,
    sortOrder,
}: Pick<FetchProgramsNamespaceArgs, 'sortBy' | 'sortOrder'>): SearchProgramsOrderByInput | undefined => {
    if (!sortBy || !searchSortFieldMap[sortBy]) {
        return undefined;
    }

    const direction = toSearchDirection(sortOrder);
    return direction ? ({ [searchSortFieldMap[sortBy]]: direction } as SearchProgramsOrderByInput) : undefined;
};

const mapExcludedCountries = (program: RawProgram): Program['excludedCountries'] =>
    ((program.excludedCountries ?? []) as RawExcludedCountry[]).map((item) => ({
        countryISOCode: (item.countryCode ?? item.countryISOCode ?? item.countryIsoCode) as CountryIsoCode,
        exclusionType: item.exclusionType,
    }));

const mapProgram = (program: RawProgram, locale?: string): ApiProgram => {
    const currentLocale = getCurrentLocale(locale);
    const title = program.editorialization?.title ?? '';
    const synopsis = program.editorialization?.synopsis ?? '';
    const keywords = (program.editorialization?.keywords ?? []).join(', ');
    const audioLanguages = Array.isArray(program.audioLanguages) ? program.audioLanguages : [];
    const originalLanguage = (program.production?.originalLanguage ?? program.originalLanguage)?.toLowerCase();
    const mappedParent = program.parent ? mapProgram(program.parent, locale) : null;
    const rightExclusivity = program.lifecycle?.rightExclusivity ?? null;

    return {
        ...program,
        originalLanguage: originalLanguage ?? null,
        audioLanguages: audioLanguages ?? null,
        awards: program.production?.awards ?? null,
        casting: program.production?.casting ?? null,
        coverUrl: program.files?.coverUrl ?? program.coverUrl ?? null,
        creditRollUrl: program.files?.creditRollUrl ?? program.creditRollUrl ?? null,
        directors: program.production?.directors ?? null,
        duration: program.duration ?? null,
        excludedCountries: mapExcludedCountries(program),
        firstBroadcastDate: program.production?.firstBroadcastDate ?? null,
        imdb: program.production?.imdb ?? null,
        isExclusive: rightExclusivity ?? false,
        isSegmentable: program.isSegmentable ?? false,
        keywords: localizedMap(keywords, currentLocale),
        masterSubtitleUrl: program.files?.masterSubtitleUrl ?? program.masterSubtitleUrl ?? null,
        masterTechnicalDetails: program.files?.masterTechnicalDetails ?? program.masterTechnicalDetails ?? null,
        masterUrl: program.files?.masterUrl ?? program.masterUrl ?? null,
        parent: mappedParent,
        productionCountryIsoCode: program.production?.productionCountry ?? null,
        publicSeller: program.publicSeller ?? (program.seller ? { legalName: program.seller.legalName } : undefined),
        published: program.lifecycle?.published ?? false,
        rightExclusivity,
        seller: program.seller,
        synopsis: localizedMap(synopsis, currentLocale),
        titles: localizedMap(title, currentLocale),
        trailerSubtitleUrl: program.files?.trailerSubtitleUrl ?? program.trailerSubtitleUrl ?? null,
        trailerSubtitles: [],
        trailerUrl: program.files?.trailerUrl ?? program.trailerUrl ?? null,
        masterSubtitles: [],
        validated: program.lifecycle?.validated,
    } as unknown as ApiProgram;
};

export async function fetchProgramsCatalog({
    page = 1,
    pageSize = 20,
    excludedGenres,
    filters,
    sortBy,
    sortOrder,
    random = false,
    skipAuth = false,
    locale,
}: FetchProgramsNamespaceArgs): Promise<{ data: ApiProgram[]; total: number }> {
    const response = await apolloClient.query({
        query: GET_PROGRAMS_CATALOG,
        fetchPolicy: 'no-cache',
        variables: {
            filter: buildCatalogFilter(filters, excludedGenres),
            orderBy: buildCatalogOrderBy({ random, sortBy, sortOrder }),
            pagination: toPagination({ page, pageSize }),
        },
        context: toHeaderContext({ locale, skipAuth }),
    });

    const output = response.data.programs.catalog;

    return {
        data: (output.items as RawProgram[]).map((program) => mapProgram(program, locale)),
        total: output.count,
    };
}

export async function fetchProgramsDashboard({
    page = 1,
    pageSize = 20,
    filters,
    sortBy,
    sortOrder,
    locale,
}: FetchProgramsNamespaceArgs): Promise<{ data: ApiProgram[]; total: number }> {
    const response = await apolloClient.query({
        query: GET_PROGRAMS_DASHBOARD,
        fetchPolicy: 'no-cache',
        variables: {
            filter: buildDashboardFilter(filters),
            orderBy: buildSearchOrderBy({ sortBy, sortOrder }),
            pagination: toPagination({ page, pageSize }),
        },
        context: toHeaderContext({ locale }),
    });

    const output = response.data.programs.dashboard;

    return {
        data: (output.items as RawProgram[]).map((program) => mapProgram(program, locale)),
        total: output.count,
    };
}

export async function fetchProgramsAdmin({
    page = 1,
    pageSize = 20,
    filters,
    sortBy,
    sortOrder,
    locale,
}: FetchProgramsNamespaceArgs): Promise<{ data: ApiProgram[]; total: number }> {
    const response = await apolloClient.query({
        query: GET_PROGRAMS_ADMIN,
        fetchPolicy: 'no-cache',
        variables: {
            filter: buildDashboardFilter(filters),
            orderBy: buildSearchOrderBy({ sortBy, sortOrder }),
            pagination: toPagination({ page, pageSize }),
        },
        context: toHeaderContext({ locale }),
    });

    const output = response.data.programs.dashboard;

    return {
        data: (output.items as RawProgram[]).map((program) => mapProgram(program, locale)),
        total: output.count,
    };
}

const buildSubProgramsArgs = ({ id, page = 1, pageSize = 100, filters, locale }: FetchSubProgramsListArgs) => ({
    page,
    pageSize,
    filters: {
        ...filters,
        parentId: id,
    },
    sortBy: 'episodeNumber',
    sortOrder: 'asc' as const,
    locale,
});

export function fetchSubProgramsCatalog({
    skipAuth = false,
    ...args
}: FetchSubProgramsListArgs): Promise<{ data: ApiProgram[]; total: number }> {
    const { page, pageSize, filters, sortBy, sortOrder, locale } = buildSubProgramsArgs(args);

    return apolloClient
        .query({
            query: GET_EPISODES_CATALOG,
            fetchPolicy: 'no-cache',
            variables: {
                filter: buildCatalogFilter(filters),
                orderBy: buildCatalogOrderBy({ sortBy, sortOrder }),
                pagination: toPagination({ page, pageSize }),
            },
            context: toHeaderContext({ locale, skipAuth }),
        })
        .then((response) => {
            const output = response.data.programs.catalog;

            return {
                data: (output.items as RawProgram[]).map((program) => mapProgram(program, locale)),
                total: output.count,
            };
        });
}

export async function fetchSubProgramsDashboard(
    args: FetchSubProgramsListArgs,
): Promise<{ data: ApiProgram[]; total: number }> {
    const { page, pageSize, filters, sortBy, sortOrder, locale } = buildSubProgramsArgs(args);
    const response = await apolloClient.query({
        query: GET_EPISODES_DASHBOARD,
        fetchPolicy: 'no-cache',
        variables: {
            filter: buildDashboardFilter(filters),
            orderBy: buildSearchOrderBy({ sortBy, sortOrder }),
            pagination: toPagination({ page, pageSize }),
        },
        context: toHeaderContext({ locale }),
    });

    const output = response.data.programs.dashboard;

    return {
        data: (output.items as RawProgram[]).map((program) => mapProgram(program, locale)),
        total: output.count,
    };
}

export async function fetchSubProgramsAdmin(
    args: FetchSubProgramsListArgs,
): Promise<{ data: ApiProgram[]; total: number }> {
    const { page, pageSize, filters, sortBy, sortOrder, locale } = buildSubProgramsArgs(args);
    const response = await apolloClient.query({
        query: GET_EPISODES_ADMIN,
        fetchPolicy: 'no-cache',
        variables: {
            filter: buildDashboardFilter(filters),
            orderBy: buildSearchOrderBy({ sortBy, sortOrder }),
            pagination: toPagination({ page, pageSize }),
        },
        context: toHeaderContext({ locale }),
    });

    const output = response.data.programs.dashboard;

    return {
        data: (output.items as RawProgram[]).map((program) => mapProgram(program, locale)),
        total: output.count,
    };
}

export async function getProgramCatalog({
    id,
    skipAuth = false,
    locale,
}: {
    id: string;
    skipAuth?: boolean;
    locale?: string;
}): Promise<ApiProgram | null> {
    const response = await apolloClient.query({
        query: GET_PROGRAM_CATALOG,
        fetchPolicy: 'no-cache',
        variables: { id },
        context: toHeaderContext({ locale, skipAuth }),
    });

    return response.data.program.catalog ? mapProgram(response.data.program.catalog as RawProgram, locale) : null;
}

export async function getProgramDashboard({
    id,
    locale,
}: {
    id: string;
    locale?: string;
}): Promise<ApiProgram | null> {
    const response = await apolloClient.query({
        query: GET_PROGRAM_DASHBOARD,
        fetchPolicy: 'no-cache',
        variables: { id },
        context: toHeaderContext({ locale }),
    });

    return response.data.program.dashboard ? mapProgram(response.data.program.dashboard as RawProgram, locale) : null;
}

export async function getProgramAdmin({
    id,
    locale,
}: {
    id: string;
    locale?: string;
}): Promise<ApiProgram | null> {
    const response = await apolloClient.query({
        query: GET_PROGRAM_ADMIN,
        fetchPolicy: 'no-cache',
        variables: { id },
        context: toHeaderContext({ locale }),
    });

    return response.data.program.dashboard ? mapProgram(response.data.program.dashboard as RawProgram, locale) : null;
}

export async function updateProgramDashboard({
    id,
    input,
    locale,
}: {
    id: string;
    input: UpdateProgramInput;
    locale?: string;
}) {
    const result = await apolloClient.mutate({
        mutation: UPDATE_PROGRAM_DASHBOARD,
        variables: {
            id,
            input,
        },
        context: toHeaderContext({ locale }),
    });

    return result.data?.program.dashboard.update ?? null;
}

export async function updateProgramOrder({ id, episodeNumber }: { id: string; episodeNumber: number }) {
    await updateProgramDashboard({
        id,
        input: { episodeNumber },
    });

    return true;
}

export async function getEpisodesPrice(id: string, excludedEpisodeIds: string[]): Promise<number | null> {
    try {
        const response = await apolloClient.query({
            query: GET_EPISODES_PRICE,
            variables: {
                id,
                excludedEpisodeIds,
            },
            fetchPolicy: 'network-only',
        });

        return response.data.program.catalog?.episodesPrice ?? 0;
    } catch {
        return null;
    }
}

export type { CatalogProgram, DashboardProgram, ProgramQuality };
